import React, { useState, useEffect, useRef, useCallback } from 'react';
import { buildApiUrl } from '../utils/apiConfig';
import { jsonGet } from '../utils/http';

export interface City {
    id: number;
    name: string;
    stateId: number;
    stateName: string;
}

interface CityComboBoxProps {
    label?: string;
    value?: City | null;
    onChange: (city: City | null) => void;
    stateId?: number;
    disabled?: boolean;
    error?: string;
    placeholder?: string;
}

export default function CityComboBox({
    label = "City",
    value,
    onChange,
    stateId,
    disabled = false,
    error,
    placeholder = "Search for a city..."
}: CityComboBoxProps) {
    const [inputValue, setInputValue] = useState(value?.name || "");
    const [options, setOptions] = useState<City[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [activeDescendantIndex, setActiveDescendantIndex] = useState(-1);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Debounce search
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastQuery = useRef<string>("");

    // Initialize input value from prop
    useEffect(() => {
        if (value) {
            setInputValue(value.name);
        } else {
            setInputValue("");
        }
    }, [value]);

    const loadCities = useCallback(async (query: string, pageNum: number, append: boolean = false) => {
        if (loading && !append) return; // Prevent concurrent fresh loads, but allow append? Actually better to lock properly.
        // Simple lock for now

        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (query) params.append('query', query);
            if (stateId) params.append('stateId', stateId.toString());
            params.append('page', pageNum.toString());
            params.append('size', '20'); // Fetch 20 at a time

            const url = buildApiUrl(`api/athletes/cities?${params.toString()}`);
            const response = await jsonGet<{ content: City[], last: boolean }>(url);

            if (response && Array.isArray(response.content)) {
                setOptions(prev => append ? [...prev, ...response.content] : response.content);
                setHasMore(!response.last);
                setPage(pageNum);
            }
        } catch (err) {
            console.error("Failed to load cities", err);
        } finally {
            setLoading(false);
        }
    }, [stateId]);

    // Initial load or reset when stateId changes
    useEffect(() => {
        setOptions([]);
        setPage(0);
        setHasMore(true);
        setActiveDescendantIndex(-1);
    }, [stateId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        setIsOpen(true);
        setPage(0);
        setHasMore(true);
        setActiveDescendantIndex(-1);

        // Clear selection if input changes
        if (value && val !== value.name) {
            onChange(null);
        }

        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            lastQuery.current = val;
            loadCities(val, 0, false);
        }, 300);
    };

    const handleFocus = () => {
        if (!disabled) {
            setIsOpen(true);
            // Load initial options if empty
            if (options.length === 0) {
                loadCities(inputValue, 0, false);
            }
        }
    };

    const handleSelect = (city: City) => {
        setInputValue(city.name);
        onChange(city);
        setIsOpen(false);
    };

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // Reset input to selected value on blur if no selection made?
                if (value) {
                    setInputValue(value.name);
                } else {
                    // If text entered but no selection, keep text? Or clear?
                    // Typical combobox: clear if not valid selection. 
                    // But we might want to allow "custom" cities? 
                    // Requirement implies selecting from list ("typing with auto suggestion so they can select it").
                    // For now, if no value selected, clear input visually to indicate invalid?
                    // Or just leave it. Let's leave it, but maybe validation handles "City Id required".
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [value]);

    // Infinite scroll
    const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loading) {
            loadCities(lastQuery.current, page + 1, true);
        }
    };

    // Grouping: The backend returns ordered by State then City.
    // We can render grouping headers in the list.
    // Since infinite scroll appends, we might break groups if a page boundary splits a state.
    // Backend sorts by state, so all cities of a state come together.
    // However, if we fetch page 1: CA cities... end with CA city. Page 2: CA city... then CO city.
    // We can detect state name change in the flattened list.

    return (
        <div className="field city-combobox" ref={wrapperRef}>
            {label && <label>{label}</label>}
            <div className="input-wrap relative">
                <input
                    ref={inputRef}
                    className={`input ${error ? 'is-error' : ''}`}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    disabled={disabled}
                    placeholder={placeholder}
                    autoComplete="new-password"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls="city-listbox"
                    aria-activedescendant={activeDescendantIndex >= 0 && options[activeDescendantIndex] ? `city-opt-${options[activeDescendantIndex].id}` : undefined}
                />

                {isOpen && (
                    <ul
                        id="city-listbox"
                        ref={listRef}
                        className="dropdown-list"
                        role="listbox"
                        onScroll={handleScroll}
                    >
                        {options.length === 0 && !loading && (
                            <li className="no-options">No cities found</li>
                        )}

                        {options.map((city, index) => {
                            const prevCity = options[index - 1];
                            const showGroup = !prevCity || prevCity.stateName !== city.stateName;

                            return (
                                <React.Fragment key={city.id}>
                                    {showGroup && (
                                        <li className="group-header" role="presentation">
                                            {city.stateName}
                                        </li>
                                    )}
                                    <li
                                        id={`city-opt-${city.id}`}
                                        className={`option ${value?.id === city.id ? 'selected' : ''} indented`}
                                        role="option"
                                        aria-selected={value?.id === city.id}
                                        onClick={() => handleSelect(city)}
                                        onMouseEnter={() => setActiveDescendantIndex(index)}
                                    >
                                        {city.name}
                                    </li>
                                </React.Fragment>
                            );
                        })}

                        {loading && <li className="loading">Loading...</li>}
                    </ul>
                )}
            </div>
            {error && <span className="error-message">{error}</span>}

            <style>{`
                .city-combobox .relative { position: relative; }
                .city-combobox .dropdown-list {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    max-height: 200px;
                    overflow-y: auto;
                    background: white;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    z-index: 10;
                    margin-top: 4px;
                    padding: 0;
                    list-style: none;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .city-combobox .option {
                    padding: 8px 12px;
                    cursor: pointer;
                }
                .city-combobox .option:hover, .city-combobox .option.selected {
                    background-color: #f0f0f0;
                }
                .city-combobox .indented {
                    padding-left: 20px;
                }
                .city-combobox .group-header {
                    padding: 4px 8px;
                    background: #e9ecef;
                    font-weight: bold;
                    font-size: 0.9em;
                    color: #495057;
                    position: sticky;
                    top: 0;
                }
                .city-combobox .no-options, .city-combobox .loading {
                    padding: 12px;
                    color: #888;
                    text-align: center;
                }
            `}</style>
        </div>
    );
}
